import React, { useCallback, useEffect, useState } from "react";
import Dropzone from "react-dropzone";
import { baseUrl } from "../../../config/apiConfig";
import { getDataFromLocalStorage } from "../../../util/localStorage";

// TODO (backend): Replace with real tenant KB endpoints when available.
// Expected: GET  /tenants/:tenantId/kb/documents  → { data: [{ id, doc_name, doc_size, upload_dt, status }] }
// Expected: POST /tenants/:tenantId/kb/upload     → multipart/form-data with field "doc_filename"
// Expected: DELETE /tenants/:tenantId/kb/documents/:docId

const FILE_ACCEPT = {
  "application/pdf": [".pdf"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
  "text/plain": [".txt"],
};

const FILE_ACCEPT_LABEL = "PDF, DOCX, TXT";

const DocIcon = () => (
  <svg className="h-5 w-5 text-[#096dea]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
  </svg>
);

const UploadIcon = () => (
  <svg className="h-8 w-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
  </svg>
);

const OnboardingStepKnowledgeBase = ({ tenantId, disabled }) => {
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [stagedFiles, setStagedFiles] = useState([]);
  const [rejectedFiles, setRejectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState("");

  const [deletingId, setDeletingId] = useState(null);

  // ── Fetch existing KB documents ──────────────────────────────────────────
  const fetchDocuments = useCallback(async () => {
    if (!tenantId) return;
    setLoadingDocs(true);
    setLoadError("");
    try {
      const token = getDataFromLocalStorage("token");
      // TODO (backend): Wire to GET /tenants/:tenantId/kb/documents
      const res = await fetch(`${baseUrl}/tenants/${tenantId}/kb/documents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to load documents (${res.status})`);
      const json = await res.json();
      const list = json?.data?.items ?? json?.data ?? json ?? [];
      setDocuments(Array.isArray(list) ? list : []);
    } catch (err) {
      setLoadError(err.message || "Could not load knowledge base documents.");
      setDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // ── Dropzone handlers ────────────────────────────────────────────────────
  const handleDrop = (accepted, rejected) => {
    setStagedFiles(accepted);
    setRejectedFiles(rejected);
    setUploadError("");
    setUploadSuccess("");
  };

  // ── Upload ───────────────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (!stagedFiles.length || !tenantId) return;
    setUploading(true);
    setUploadError("");
    setUploadSuccess("");

    try {
      const token = getDataFromLocalStorage("token");
      const formData = new FormData();
      stagedFiles.forEach((f) => formData.append("doc_filename", f));

      // TODO (backend): Wire to POST /tenants/:tenantId/kb/upload
      const res = await fetch(`${baseUrl}/tenants/${tenantId}/kb/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message || `Upload failed (${res.status})`);
      }

      setUploadSuccess(`${stagedFiles.length} file${stagedFiles.length > 1 ? "s" : ""} uploaded successfully.`);
      setStagedFiles([]);
      setRejectedFiles([]);
      await fetchDocuments();
    } catch (err) {
      setUploadError(err.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────
  const handleDelete = async (doc) => {
    if (!tenantId) return;
    setDeletingId(doc.id);
    try {
      const token = getDataFromLocalStorage("token");
      // TODO (backend): Wire to DELETE /tenants/:tenantId/kb/documents/:docId
      const res = await fetch(`${baseUrl}/tenants/${tenantId}/kb/documents/${doc.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      await fetchDocuments();
    } catch (err) {
      setUploadError(err.message || "Delete failed.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-500">
        Upload business documents to build the tenant's knowledge base for Smart Document Search.
        Supported formats: {FILE_ACCEPT_LABEL}.
      </p>

      {/* Drop zone */}
      {!disabled ? (
        <Dropzone onDrop={handleDrop} accept={FILE_ACCEPT} multiple>
          {({ getRootProps, getInputProps, isDragActive, isDragReject }) => (
            <div
              {...getRootProps()}
              className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
                isDragReject
                  ? "border-rose-300 bg-rose-50"
                  : isDragActive
                  ? "border-[#096dea] bg-[#eef5ff]"
                  : "border-slate-200 bg-slate-50 hover:border-[#096dea] hover:bg-[#eef5ff]/50"
              }`}
            >
              <input {...getInputProps()} />
              <UploadIcon />
              <p className="mt-3 text-sm font-semibold text-slate-700">
                {isDragActive ? "Drop files here…" : "Drag & drop files or click to browse"}
              </p>
              <p className="mt-1 text-xs text-slate-400">{FILE_ACCEPT_LABEL} · Multiple files supported</p>
            </div>
          )}
        </Dropzone>
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-6 py-8 text-center">
          <p className="text-sm text-slate-400">Document upload is disabled — read-only access.</p>
        </div>
      )}

      {/* Staged files */}
      {stagedFiles.length > 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Ready to upload
          </p>
          <ul className="space-y-2">
            {stagedFiles.map((f, i) => (
              <li key={i} className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2.5">
                <div className="flex items-center gap-2 min-w-0">
                  <DocIcon />
                  <span className="truncate text-sm font-medium text-slate-900">{f.name}</span>
                </div>
                <span className="shrink-0 text-xs text-slate-400">
                  {(f.size / 1024).toFixed(0)} KB
                </span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={handleUpload}
            disabled={uploading}
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#096dea] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0959bb] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {uploading ? "Uploading…" : `Upload ${stagedFiles.length} file${stagedFiles.length > 1 ? "s" : ""}`}
          </button>
        </div>
      ) : null}

      {/* Rejected files */}
      {rejectedFiles.length > 0 ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3">
          <p className="text-xs font-semibold text-rose-700">Unsupported file type</p>
          <ul className="mt-1 space-y-0.5">
            {rejectedFiles.map(({ file }, i) => (
              <li key={i} className="text-xs text-rose-600">{file.name}</li>
            ))}
          </ul>
          <p className="mt-1 text-xs text-rose-500">Accepted: {FILE_ACCEPT_LABEL}</p>
        </div>
      ) : null}

      {/* Feedback messages */}
      {uploadSuccess ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800">
          {uploadSuccess}
        </div>
      ) : null}
      {uploadError ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {uploadError}
        </div>
      ) : null}

      {/* Document list */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-slate-950">
            Knowledge base documents
            {documents.length > 0 ? (
              <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
                {documents.length}
              </span>
            ) : null}
          </h4>
          {!loadingDocs ? (
            <button
              type="button"
              onClick={fetchDocuments}
              className="text-xs font-medium text-[#096dea] hover:underline"
            >
              Refresh
            </button>
          ) : null}
        </div>

        {loadingDocs ? (
          <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 py-10">
            <p className="text-sm text-slate-400">Loading documents…</p>
          </div>
        ) : loadError ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {loadError}
          </div>
        ) : documents.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white py-10 text-center">
            <DocIcon />
            <p className="mt-3 text-sm font-medium text-slate-600">No documents yet</p>
            <p className="mt-1 text-xs text-slate-400">
              {disabled
                ? "No knowledge base documents have been uploaded for this tenant."
                : "Upload documents above to build the knowledge base."}
            </p>
          </div>
        ) : (
          <ul className="space-y-2">
            {documents.map((doc) => (
              <li
                key={doc.id}
                className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#eef5ff]">
                    <DocIcon />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900">
                      {doc.doc_name || doc.name || "Document"}
                    </p>
                    <p className="text-xs text-slate-400">
                      {doc.doc_size || doc.size || ""}
                      {doc.upload_dt || doc.created_at
                        ? ` · ${doc.upload_dt || doc.created_at}`
                        : ""}
                    </p>
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  {doc.status ? (
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${
                        doc.status === "indexed"
                          ? "bg-emerald-100 text-emerald-800 ring-emerald-200"
                          : doc.status === "processing"
                          ? "bg-amber-100 text-amber-800 ring-amber-200"
                          : "bg-slate-100 text-slate-600 ring-slate-200"
                      }`}
                    >
                      {doc.status}
                    </span>
                  ) : null}
                  {!disabled ? (
                    <button
                      type="button"
                      onClick={() => handleDelete(doc)}
                      disabled={deletingId === doc.id}
                      className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {deletingId === doc.id ? "Removing…" : "Remove"}
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default OnboardingStepKnowledgeBase;
